"use client";

import * as AvatarPrimitive from "@radix-ui/react-avatar";
import type { HTMLAttributes, Ref } from "react";

import { cn } from "../../utils/cn.util";
import { UserIcon } from "../icons";
import { useAvatarContext } from "./Root";
import { avatarStyles } from "./styles";
import { getBackgroundColor, getColor, shortenText } from "./utils";

export type AvatarFallbackProps = HTMLAttributes<HTMLDivElement> & {
  text?: string;
  ref?: Ref<HTMLDivElement>;
};

/**
 * Initials (or a user icon) over the avatar's image, following the `data-loaded` of the `Avatar.Image` before it (its
 * `peer`): transparent while the image loads (the image pulses meanwhile) and once it has, shown when it fails or
 * there's no image at all. So it goes after `Avatar.Image`, as every avatar already writes it.
 */
export const Fallback = ({ ref, className, text, children, ...props }: AvatarFallbackProps) => {
  const { size } = useAvatarContext();

  const fallback = (text && shortenText(text)) || (
    <UserIcon className={cn(avatarStyles({ placeholderSize: size }), "text-neutral-50", className)} />
  );

  return (
    <div
      ref={ref}
      className={cn(
        "absolute flex size-full items-center justify-center rounded-full transition-all duration-300 ease-in-out",
        text ? [getColor(text), getBackgroundColor(text)] : "bg-primary-95 text-primary-40",
        "peer-data-[loaded=false]:opacity-0 peer-data-[loaded=true]:opacity-0",
        className
      )}
      {...props}
    >
      {children ?? fallback}
    </div>
  );
};

Fallback.displayName = AvatarPrimitive.Fallback.displayName;
